import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/api/client";

/**
 * Handles the X/Twitter OAuth 2.0 callback.
 * Twitter redirects here with ?code=&state= (or ?error=&error_description=).
 * We forward the params to /api/auth/twitter/exchange which finalizes the flow.
 */
export default function TwitterCallback() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("Completing X authorization…");
  const [username, setUsername] = useState("");
  const ranRef = useRef(false); // guard against React 19 strict-mode double effect

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const params = new URLSearchParams(search);
    const code = params.get("code");
    const state = params.get("state");
    const err = params.get("error");

    if (err) {
      setStatus("error");
      setMessage(err === "access_denied" ? "You cancelled the X authorization." : `Authorization failed (${err}).`);
      return;
    }
    if (!code || !state) {
      setStatus("error");
      setMessage("Missing authorization code. Please try connecting again.");
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/twitter/exchange", { code, state });
        setUsername(data.username || "");
        setStatus("success");
        setMessage("Your X account is now connected. Tweets will auto-post when you publish an article.");
      } catch (e) {
        const detail = e.response?.data?.detail;
        const msg =
          (detail && typeof detail === "object" && detail.message) ||
          formatApiErrorDetail(detail) ||
          e.message ||
          "Could not complete authorization.";
        setStatus("error");
        setMessage(msg);
      }
    })();
  }, [search]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-6">
      <div className="max-w-md w-full border border-slate-800 rounded-xl p-8 bg-gradient-to-b from-slate-900/60 to-transparent text-center">
        {status === "loading" && (
          <>
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-sky-400 mb-4" />
            <p className="font-serif text-xl text-sky-100">Connecting X…</p>
            <p className="text-sm text-slate-500 mt-2">{message}</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-400 mb-4" />
            <p className="font-serif text-2xl text-sky-100">X connected ✓</p>
            {username && <p className="text-sm text-sky-300 font-mono mt-1">@{username}</p>}
            <p className="text-sm text-slate-400 mt-3">{message}</p>
            <button
              data-testid="btn-twitter-callback-continue"
              onClick={() => navigate("/app/settings", { replace: true })}
              className="mt-6 px-5 py-2.5 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded font-medium inline-flex items-center gap-2"
            >
              Back to Settings <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="w-10 h-10 mx-auto text-red-400 mb-4" />
            <p className="font-serif text-2xl text-sky-100">Authorization failed</p>
            <p className="text-sm text-slate-400 mt-3" data-testid="twitter-callback-error">{message}</p>
            <button
              data-testid="btn-twitter-callback-retry"
              onClick={() => navigate("/app/settings", { replace: true })}
              className="mt-6 px-5 py-2.5 border border-sky-400/60 text-sky-200 hover:bg-sky-500/10 rounded font-medium inline-flex items-center gap-2"
            >
              Back to Settings <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
